import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect, useId } from 'react';
import ReactDOM from 'react-dom';
import { IonIcon, IonText } from '@ionic/react';
import { closeCircle, addCircleOutline, chevronDownOutline } from 'ionicons/icons';
import './TagsInput.css';
import { useDebounce } from '../../utils/useDebounce';

const BLUR_CLOSE_DELAY_MS = 200;
const LARGE_LIST_MAX_SUGGESTIONS = 20;
const DROPDOWN_OFFSET = 4;

const PILL_COLORS = [
    { bg: 'rgba(99,102,241,0.18)', border: 'rgba(99,102,241,0.45)', text: '#a5b4fc' },   // indigo
    { bg: 'rgba(244,114,182,0.18)', border: 'rgba(244,114,182,0.45)', text: '#f9a8d4' },  // pink
    { bg: 'rgba(52,211,153,0.18)', border: 'rgba(52,211,153,0.45)', text: '#6ee7b7' },    // emerald
    { bg: 'rgba(251,191,36,0.18)', border: 'rgba(251,191,36,0.45)', text: '#fcd34d' },    // amber
    { bg: 'rgba(96,165,250,0.18)', border: 'rgba(96,165,250,0.45)', text: '#93c5fd' },    // blue
    { bg: 'rgba(167,139,250,0.18)', border: 'rgba(167,139,250,0.45)', text: '#c4b5fd' },  // violet
    { bg: 'rgba(248,113,113,0.18)', border: 'rgba(248,113,113,0.45)', text: '#fca5a5' },   // red
    { bg: 'rgba(45,212,191,0.18)', border: 'rgba(45,212,191,0.45)', text: '#5eead4' },    // teal
];

function pillColor(index: number) {
    return PILL_COLORS[index % PILL_COLORS.length];
}

/* ── Props ── */
export interface TagsInputProps<T> {
    selectedItems: T[];
    onSelectedItemsChange: (items: T[]) => void;
    availableItems: T[];
    getItemLabel?: (item: T) => string;
    getItemKey?: (item: T) => string;
    label?: string;
    placeholder?: string;
    emptyStateText?: string;
    noResultsText?: string;
    maxSuggestions?: number;
    disabled?: boolean;
    debounceMs?: number;
    /** If true, suggestions render via a portal with fixed positioning (avoids clipping). Default true. */
    portal?: boolean;
    /** If availableItems.length exceeds this, results are hard-limited for performance. Default 500. */
    virtualizeThreshold?: number;
    /** Optional custom chip renderer. Receives item, color object, and remove handler. */
    renderChip?: (item: T, color: typeof PILL_COLORS[number], onRemove: () => void) => React.ReactNode;
    /** Debug mode: enable instrumentation logs and mount counts. Default false. */
    debug?: boolean;
}

const TagsInput = <T,>({
    selectedItems,
    onSelectedItemsChange,
    availableItems,
    getItemLabel,
    getItemKey,
    label = 'Tags',
    placeholder = 'Zoek en selecteer...',
    emptyStateText = 'Geen items geselecteerd',
    noResultsText = 'Geen resultaten',
    maxSuggestions = 8,
    disabled = false,
    debounceMs = 200,
    portal = true,
    virtualizeThreshold = 500,
    renderChip,
    debug = false,
}: TagsInputProps<T>) => {
    const reactId = useId();
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, debounceMs);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const blurTimeoutRef = useRef<number | undefined>(undefined);
    const rafRef = useRef<number | null>(null);

    /* ── Dropdown position (for portal mode) ── */
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    // Track last computed dropdown position to avoid unnecessary setState
    const lastDropdownPosRef = useRef(dropdownPos);

    // Throttled update using requestAnimationFrame to avoid heavy work on scroll/resize
    // (note: we re-declare updateDropdownPos to capture lastDropdownPosRef)
    const updateDropdownPos = useCallback(() => {
        if (rafRef.current != null) return; // already scheduled
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const newPos = {
                top: rect.bottom + DROPDOWN_OFFSET,
                left: rect.left,
                width: rect.width,
            };
            const last = lastDropdownPosRef.current;
            // shallow compare to avoid state churn
            if (last.top !== newPos.top || last.left !== newPos.left || last.width !== newPos.width) {
                lastDropdownPosRef.current = newPos;
                setDropdownPos(newPos);
            }
        });
    }, []);

    // Recompute position when suggestions become visible or on scroll/resize
    useLayoutEffect(() => {
        if (!showSuggestions || !portal) return;
        updateDropdownPos();

        const onUpdate = () => updateDropdownPos();
        window.addEventListener('scroll', onUpdate, true);
        window.addEventListener('resize', onUpdate);
        return () => {
            window.removeEventListener('scroll', onUpdate, true);
            window.removeEventListener('resize', onUpdate);
            // cancel any pending RAF
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [showSuggestions, portal, updateDropdownPos]);

    /* ── Resolvers ── */
    const resolveLabel = useCallback(
        (item: T) => (getItemLabel ? getItemLabel(item) : String(item)),
        [getItemLabel],
    );

    const resolveKey = useCallback(
        (item: T) => (getItemKey ? getItemKey(item) : String(item)),
        [getItemKey],
    );

    /* ── Derived lists ── */
    const selectedKeys = useMemo(
        () => new Set(selectedItems.map(resolveKey)),
        [selectedItems, resolveKey],
    );

    const unselectedOptions = useMemo(
        () => availableItems.filter(item => !selectedKeys.has(resolveKey(item))),
        [availableItems, selectedKeys, resolveKey],
    );

    const isLargeList = availableItems.length > virtualizeThreshold;
    const visibleLimit = isLargeList ? Math.min(maxSuggestions, LARGE_LIST_MAX_SUGGESTIONS) : maxSuggestions;

    const matchingOptions = useMemo(() => {
        const query = debouncedSearch.trim().toLowerCase();
        return query.length === 0
            ? unselectedOptions
            : unselectedOptions.filter(item =>
                resolveLabel(item).toLowerCase().includes(query),
            );
    }, [unselectedOptions, debouncedSearch, resolveLabel]);

    const filteredOptions = useMemo(
        () => matchingOptions.slice(0, visibleLimit),
        [matchingOptions, visibleLimit],
    );

    const hiddenCount = matchingOptions.length - filteredOptions.length;

    useEffect(() => {
        if (highlightedIndex >= filteredOptions.length) {
            setHighlightedIndex(0);
        }
    }, [filteredOptions, highlightedIndex]);

    /* ── Handlers ── */
    const logDebug = useCallback((message: string, payload?: unknown) => {
        if (!debug) return;
        if (payload === undefined) {
            console.debug('[TagsInput]', message);
            return;
        }
        console.debug('[TagsInput]', message, payload);
    }, [debug]);

    const handleAdd = useCallback(
        (item: T) => {
            onSelectedItemsChange([...selectedItems, item]);
            setSearchInput('');
            setShowSuggestions(true);
            setHighlightedIndex(0);
            inputRef.current?.focus();
            logDebug('item added', resolveLabel(item));
        },
        [selectedItems, onSelectedItemsChange, logDebug, resolveLabel],
    );

    const handleRemove = useCallback(
        (item: T) => {
            const key = resolveKey(item);
            onSelectedItemsChange(selectedItems.filter(i => resolveKey(i) !== key));
        },
        [selectedItems, onSelectedItemsChange, resolveKey],
    );

    const handleBlur = useCallback(() => {
        // clear existing timeout if any
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = undefined;
        }
        blurTimeoutRef.current = window.setTimeout(() => {
            setShowSuggestions(false);
            blurTimeoutRef.current = undefined;
        }, BLUR_CLOSE_DELAY_MS);
    }, []);

    const handleFocus = useCallback(() => {
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = undefined;
        }
        if (!disabled) {
            setShowSuggestions(true);
        }
    }, [disabled]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!showSuggestions || filteredOptions.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setHighlightedIndex(prev => (prev + 1) % filteredOptions.length);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setHighlightedIndex(prev =>
                        (prev - 1 + filteredOptions.length) % filteredOptions.length,
                    );
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (filteredOptions[highlightedIndex]) {
                        handleAdd(filteredOptions[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    setShowSuggestions(false);
                    break;
            }
        },
        [showSuggestions, filteredOptions, highlightedIndex, handleAdd],
    );

    // Close dropdown when clicking outside (for portal mode)
    useEffect(() => {
        if (!showSuggestions || !portal) return;
        const handleOutsideClick = (e: PointerEvent) => {
            const target = e.target as Node;
            if (
                containerRef.current &&
                (containerRef.current.contains(target) || dropdownRef.current?.contains(target))
            ) {
                return;
            }
            setShowSuggestions(false);
            logDebug('closed suggestions by outside interaction');
        };
        document.addEventListener('pointerdown', handleOutsideClick, true);
        return () => {
            document.removeEventListener('pointerdown', handleOutsideClick, true);
        };
    }, [showSuggestions, portal, logDebug]);

    // Clear any pending timeout / raf on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                window.clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = undefined;
            }
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, []);

    const hasSelected = selectedItems.length > 0;
    const allAssigned = unselectedOptions.length === 0;
    const listboxId = `tags-listbox-${reactId}`;

    /* ── Suggestions dropdown content ── */
    const renderDropdown = () => {
        if (!showSuggestions || disabled) return null;

        const content = (
            <div
                ref={dropdownRef}
                className={`tags-suggestions-dropdown${portal ? ' tags-suggestions-portal' : ''}`}
                role="listbox"
                id={listboxId}
                style={portal ? {
                    position: 'fixed',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                    zIndex: 9999,
                } : undefined}
            >
                {filteredOptions.length > 0 ? (
                    <>
                        {filteredOptions.map((item, index) => {
                            const key = resolveKey(item);
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    role="option"
                                    id={`${listboxId}-opt-${index}`}
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => handleAdd(item)}
                                    className={`tags-suggestion-item${index === highlightedIndex ? ' is-highlighted' : ''}`}
                                    aria-selected={index === highlightedIndex}
                                >
                                    <IonIcon icon={addCircleOutline} className="tags-suggestion-icon" />
                                    <span>{resolveLabel(item)}</span>
                                </button>
                            );
                        })}
                        {hiddenCount > 0 && (
                            <div className="tags-suggestion-more">
                                <IonText color="medium">
                                    +{hiddenCount} meer — typ om te verfijnen
                                </IonText>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="tags-suggestion-empty">
                        <IonText color="medium">
                            {allAssigned ? 'Alle opties zijn al geselecteerd' : noResultsText}
                        </IonText>
                    </div>
                )}
            </div>
        );

        if (portal) {
            return ReactDOM.createPortal(content, document.body);
        }
        return content;
    };

    return (
        <div
            ref={containerRef}
            className={`tags-input-container${disabled ? ' is-disabled' : ''}`}
        >
            {label && <span className="tags-input-label">{label}</span>}

            {/* Selected chips */}
            <div className="tags-chips-area" aria-live="polite">
                {hasSelected ? (
                    selectedItems.map((item, idx) => {
                        const color = pillColor(idx);

                        if (renderChip) {
                            return (
                                <React.Fragment key={resolveKey(item)}>
                                    {renderChip(item, color, () => handleRemove(item))}
                                </React.Fragment>
                            );
                        }

                        return (
                            <span
                                key={resolveKey(item)}
                                className="tags-chip"
                                style={{
                                    background: color.bg,
                                    borderColor: color.border,
                                    color: color.text,
                                }}
                            >
                                <span className="tags-chip-label">{resolveLabel(item)}</span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        className="tags-chip-remove"
                                        onClick={() => handleRemove(item)}
                                        aria-label={`Verwijder ${resolveLabel(item)}`}
                                        style={{ color: color.text }}
                                    >
                                        <IonIcon icon={closeCircle} />
                                    </button>
                                )}
                            </span>
                        );
                    })
                ) : (
                    <span className="tags-empty-state">
                        <IonText color="medium">{emptyStateText}</IonText>
                    </span>
                )}
            </div>

            {/* Search input */}
            {!disabled && (
                <div className="tags-search-wrapper">
                    <div className="tags-search-inner">
                        <input
                            ref={inputRef}
                            value={searchInput}
                            placeholder={placeholder}
                            onChange={e => setSearchInput(e.target.value)}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            className="tags-search-input"
                            disabled={disabled}
                            role="combobox"
                            aria-expanded={showSuggestions}
                            aria-controls={listboxId}
                            aria-activedescendant={
                                showSuggestions && filteredOptions.length > 0
                                    ? `${listboxId}-opt-${highlightedIndex}`
                                    : undefined
                            }
                            autoComplete="off"
                        />
                        <IonIcon
                            icon={chevronDownOutline}
                            className={`tags-search-chevron${showSuggestions ? ' is-open' : ''}`}
                        />
                    </div>
                </div>
            )}

            {/* Suggestions dropdown */}
            {renderDropdown()}
        </div>
    );
};

export default TagsInput;

